<template>
  <div class="LLFileModal" :style="modalStyle" data-e2e="fileModal">
    <div class="LLFileModal__background" @click="onBackplateClick"></div>
    <div class="LLFileModal__block">
      <div class="modal-content">
        <header class="LLFileModal__header">
          <FileModalOwner v-if="owner" :owner="owner" class="LLFileModal__author" />
          <div class="LLFileModal__header-actions">
            <div class="LLFileModal__counter">
              <template v-if="currentIsImage">{{
                $t('image_counter', { current: currentIndex + 1, all: files.length })
              }}</template>
              <template v-else-if="currentIsVideo">{{
                $t('video_counter', { current: currentIndex + 1, all: files.length })
              }}</template>
            </div>
            <LLButton
              class="LLFileModal__close-button"
              primary-transparent
              tiny
              no-x-padding
              data-e2e="closeModal"
              @click="close"
            >
              <CloseRegularIcon />
            </LLButton>
          </div>
        </header>
        <div class="LLFileModal__body">
          <div
            ref="media"
            class="LLFileModal__media"
            :class="{ LLFileModal__media_zoomable: currentIsImage }"
          >
            <button
              v-if="hasPrevFile"
              type="button"
              class="LLFileModal__nav LLFileModal__nav_prev"
              data-e2e="prevFile"
              @click="prevFile"
            >
              <CircleArrowLeftIcon class="LLFileModal__nav-icon" />
            </button>
            <div
              ref="track"
              class="LLFileModal__media-track"
              :class="{
                'LLFileModal__media-track_dragging': dragging,
                'LLFileModal__media-track_anim': dragAnim,
                'LLFileModal__media-track_settle': zoomSettling
              }"
              :style="mediaTrackStyle"
              @pointerdown="onPointerDown"
            >
              <LLFileModalImage
                v-if="currentIsImage"
                :key="selectedFile.id"
                fill
                class="LLFileModal__file"
                :class="mediaAnimationClass"
                :file="selectedFile"
              />
              <LLFileModalVideo
                v-else-if="currentIsVideo"
                :key="selectedFile.id"
                class="LLFileModal__file"
                :class="mediaAnimationClass"
                :file="selectedFile"
              />
            </div>
            <button
              v-if="hasNextFile"
              type="button"
              class="LLFileModal__nav LLFileModal__nav_next"
              data-e2e="nextFile"
              @click="nextFile"
            >
              <CircleArrowRightIcon class="LLFileModal__nav-icon" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LLFileModalImage from './files/LLFileModalImage'
import LLFileModalVideo from './files/LLFileModalVideo'
import CircleArrowLeftIcon from '@/assets/icons/CircleArrowLeftIcon'
import CircleArrowRightIcon from '@/assets/icons/CircleArrowRightIcon'
import CloseRegularIcon from '@/assets/icons/CloseRegularIcon'
import LLButton from '@/components/common/ui-components/LLButton'
import FileModalOwner from '@/components/common/fileGallery/FileModalOwner'
import { preloadOriginal } from '@/utils/imageLoadCache'

const ZOOM_MIN = 1
const ZOOM_MAX = 3
const DOUBLE_TAP_MS = 300
const TAP_MOVE_TOLERANCE = 30
const DEFAULT_Z_INDEX = 10000

export default {
  name: 'LLFileModal',
  components: {
    CircleArrowRightIcon,
    CircleArrowLeftIcon,
    LLFileModalVideo,
    LLFileModalImage,
    FileModalOwner,
    LLButton,
    CloseRegularIcon
  },
  props: {
    selectedFile: { type: [Object, null], default: null },
    files: {
      type: Array,
      default() {
        return []
      }
    },
    zIndex: { type: Number, default: null },
    closeOnBackdrop: { type: Boolean, default: true },
    owner: { type: Object, default: null }
  },
  data: () => ({
    mediaAnimated: false,
    slideDir: 'next',
    dragging: false,
    dragAnim: false,
    dragOffset: 0,
    drag: null,
    dragTimers: [],
    pointers: {},
    pinchStart: null,
    panStart: null,
    zoomScale: 1,
    zoomX: 0,
    zoomY: 0,
    zoomSettling: false,
    lastTap: null
  }),
  computed: {
    selected() {
      return this.selectedFile || null
    },
    modalStyle() {
      return {
        zIndex: this.zIndex ?? DEFAULT_Z_INDEX
      }
    },
    currentIndex() {
      return this.files.findIndex((f) => f && f.id === this.selected?.id)
    },
    currentFileType() {
      return this.selected && this.selected.mimetype ? this.selected.mimetype.substr(0, 4) : ''
    },
    currentIsImage() {
      return this.currentFileType === 'imag'
    },
    currentIsVideo() {
      return this.currentFileType === 'vide'
    },
    hasSeveralFiles() {
      return this.files && this.files.length > 1
    },
    hasPrevFile() {
      return this.currentIndex > 0
    },
    hasNextFile() {
      return this.currentIndex > -1 && this.currentIndex < this.files.length - 1
    },
    mediaAnimationClass() {
      if (!this.mediaAnimated) {
        return null
      }
      return this.slideDir === 'prev' ? 'LLFileModal__file_prev' : 'LLFileModal__file_next'
    },
    mediaTrackStyle() {
      if (this.zoomScale > 1) {
        return {
          transform: `translate3d(${this.zoomX}px, ${this.zoomY}px, 0) scale(${this.zoomScale})`
        }
      }
      if (!this.dragging && !this.dragAnim && !this.dragOffset) {
        return null
      }
      return {
        transform: `translate3d(${this.dragOffset}px, 0, 0)`
      }
    }
  },
  watch: {
    files: {
      handler() {
        this.$nextTick(() => {
          this.preloadNeighbours()
        })
      }
    },
    selectedFile: {
      handler() {
        // Reset zoom on every file change, but keep the drag transform intact:
        // slideOutAndChange places the incoming file off-screen and animates it
        // in, and cancelling the drag here would snap it straight to the centre.
        this.resetZoom()
        this.$nextTick(() => {
          this.preloadNeighbours()
        })
      },
      immediate: true
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    document.body.classList.add('block-scroll')
  },
  beforeDestroy() {
    this.clearPointerListeners()
    this.pointers = {}
    this.drag = null
    this.panStart = null
    this.pinchStart = null
    this.dragTimers.forEach((timer) => clearTimeout(timer))
    this.dragTimers = []
    document.removeEventListener('keydown', this.onKeydown)
    document.body.classList.remove('block-scroll')
  },
  methods: {
    preloadNeighbours() {
      const index = this.currentIndex
      if (!this.files || this.files.length < 2 || index < 0) {
        return
      }
      const candidates = []
      if (index > 0) {
        candidates.push(this.files[index - 1])
      }
      if (index < this.files.length - 1) {
        candidates.push(this.files[index + 1])
      }
      candidates.forEach((file) => {
        if (this.isImageLike(file)) {
          preloadOriginal(file).catch(() => {})
        }
      })
    },
    isImageLike(file) {
      return !!(file && file.mimetype && file.mimetype.substr(0, 4) === 'imag')
    },
    onKeydown(e) {
      if (e.key === 'Escape' && !this.isTyping(e)) {
        this.close()
        return
      }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
        return
      }
      if (this.isArrowNavigationBlocked(e)) {
        return
      }
      if (e.key === 'ArrowLeft') {
        if (!this.hasPrevFile) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        this.prevFile()
        return
      }
      if (!this.hasNextFile) {
        return
      }
      e.preventDefault()
      e.stopPropagation()
      this.nextFile()
    },
    isTyping(e) {
      const tag = e.target && e.target.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable)
    },
    // Arrow keys must not hijack interaction with editable fields, media, or
    // nested dialogs/popovers rendered inside the modal.
    isArrowNavigationBlocked(e) {
      if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
        return true
      }
      if (document.querySelector('.LLConfirmModal')) {
        return true
      }
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [e.target]
      const arrowRoles = [
        'textbox',
        'searchbox',
        'slider',
        'spinbutton',
        'listbox',
        'combobox',
        'menu',
        'menubar',
        'radiogroup',
        'tablist',
        'tree',
        'grid'
      ]
      return path.some((node) => {
        if (!node || node.nodeType !== 1) {
          return false
        }
        const tag = node.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'OPTION') {
          return true
        }
        if (tag === 'VIDEO' || tag === 'AUDIO') {
          return true
        }
        if (node.isContentEditable) {
          return true
        }
        if (node.hasAttribute && node.hasAttribute('data-file-modal-keynav-ignore')) {
          return true
        }
        const role = node.getAttribute && node.getAttribute('role')
        return !!(role && arrowRoles.includes(role))
      })
    },
    onBackplateClick() {
      if (this.closeOnBackdrop) {
        this.close()
      }
    },
    onPointerDown(e) {
      if (e.pointerType !== 'touch') {
        return
      }
      if (this.dragAnim) {
        return
      }
      if (this.isOnVideoControls(e)) {
        return
      }
      const touchCount = Object.keys(this.pointers).length
      if (touchCount > 0) {
        // Additional finger: only two-finger pinch on images is supported
        if (touchCount >= 2 || !this.currentIsImage) {
          return
        }
        this.pointers[e.pointerId] = this.buildPointerEntry(e)
        this.cancelFingerGesture()
        this.startPinch()
        return
      }
      if (this.currentIsImage && this.isDoubleTap(e)) {
        this.lastTap = null
        this.toggleZoom(e.clientX, e.clientY)
        return
      }
      this.pointers[e.pointerId] = this.buildPointerEntry(e)
      window.addEventListener('pointermove', this.onPointerMove)
      window.addEventListener('pointerup', this.onPointerUp)
      window.addEventListener('pointercancel', this.onPointerCancel)
      // Zoomed images pan instead of navigating between files
      if (this.currentIsImage && this.zoomScale > 1) {
        this.startPanFromPointer(e.pointerId)
        return
      }
      if (!this.hasSeveralFiles) {
        return
      }
      const track = this.$refs.track
      const width = track ? track.getBoundingClientRect().width : this.$el.clientWidth
      this.drag = {
        id: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startTime: Date.now(),
        lastX: e.clientX,
        lastTime: Date.now(),
        width: width || 1
      }
      this.dragging = true
      this.dragAnim = false
      this.dragOffset = 0
    },
    buildPointerEntry(e) {
      return {
        x: e.clientX,
        y: e.clientY,
        sx: e.clientX,
        sy: e.clientY,
        t: Date.now()
      }
    },
    isDoubleTap(e) {
      const last = this.lastTap
      if (!last) {
        return false
      }
      if (Date.now() - last.time > DOUBLE_TAP_MS) {
        return false
      }
      if (Math.abs(e.clientX - last.x) > TAP_MOVE_TOLERANCE) {
        return false
      }
      return Math.abs(e.clientY - last.y) <= TAP_MOVE_TOLERANCE
    },
    onPointerMove(e) {
      const pointer = this.pointers[e.pointerId]
      if (!pointer) {
        return
      }
      pointer.x = e.clientX
      pointer.y = e.clientY
      if (this.pinchStart) {
        this.updatePinch()
        return
      }
      if (this.panStart && e.pointerId === this.panStart.id) {
        this.updatePan(e)
        return
      }
      if (this.drag && e.pointerId === this.drag.id) {
        const dx = e.clientX - this.drag.startX
        const dy = e.clientY - this.drag.startY
        // Abort when the gesture becomes mostly vertical (e.g. scroll intent)
        if (Math.abs(dy) > 40 && Math.abs(dy) > Math.abs(dx) * 1.4) {
          this.cancelDrag()
          return
        }
        this.drag.lastX = e.clientX
        this.drag.lastTime = Date.now()
        this.dragOffset = this.applyRubberband(dx)
      }
    },
    onPointerUp(e) {
      const pointer = this.pointers[e.pointerId]
      if (!pointer) {
        return
      }
      if (this.pinchStart && Object.keys(this.pointers).length >= 2) {
        this.handlePinchEnd(e)
        return
      }
      if (this.panStart && e.pointerId === this.panStart.id) {
        const moved = Math.abs(e.clientX - pointer.sx) + Math.abs(e.clientY - pointer.sy)
        const quick = Date.now() - pointer.t < DOUBLE_TAP_MS
        this.panStart = null
        this.releasePointer(e)
        if (this.currentIsImage && quick && moved <= TAP_MOVE_TOLERANCE) {
          this.lastTap = { time: Date.now(), x: e.clientX, y: e.clientY }
        }
        return
      }
      if (this.drag && e.pointerId === this.drag.id) {
        this.handleNavEnd(e)
        return
      }
      // Plain tap (no drag started) — remember it for double-tap detection
      const moved = Math.abs(e.clientX - pointer.sx) + Math.abs(e.clientY - pointer.sy)
      const quick = Date.now() - pointer.t < DOUBLE_TAP_MS
      this.releasePointer(e)
      if (this.currentIsImage && quick && moved <= TAP_MOVE_TOLERANCE) {
        this.lastTap = { time: Date.now(), x: e.clientX, y: e.clientY }
      }
    },
    onPointerCancel(e) {
      if (!this.pointers[e.pointerId]) {
        return
      }
      this.cancelFingerGesture()
      this.releasePointer(e)
    },
    handlePinchEnd(e) {
      this.pinchStart = null
      delete this.pointers[e.pointerId]
      const ids = Object.keys(this.pointers)
      if (ids.length === 1) {
        // Remaining finger keeps controlling the zoomed image
        if (this.zoomScale > 1 && this.currentIsImage) {
          this.startPanFromPointer(ids[0])
        }
        return
      }
      if (ids.length === 0) {
        this.clearPointerListeners()
        this.dragging = false
        this.drag = null
      }
    },
    handleNavEnd(e) {
      const drag = this.drag
      const dx = e.clientX - drag.startX
      const dy = e.clientY - drag.startY
      const moveDt = Date.now() - drag.lastTime || 1
      const velocity = (e.clientX - drag.lastX) / moveDt
      const width = drag.width
      const canNext = this.currentIndex < this.files.length - 1
      const canPrev = this.currentIndex > 0
      const moved = Math.abs(dx) + Math.abs(dy)
      const quick = Date.now() - drag.startTime < DOUBLE_TAP_MS
      const travel = Math.abs(this.dragOffset)
      const wasTap = quick && moved <= TAP_MOVE_TOLERANCE

      this.releasePointer(e)

      if (wasTap || Math.abs(this.dragOffset) < 1) {
        if (this.currentIsImage) {
          this.lastTap = { time: Date.now(), x: e.clientX, y: e.clientY }
        }
        return
      }
      // Decide where to go
      let decision = 0
      if (dx < 0 && canNext && travel > Math.max(width * 0.22, 60)) {
        decision = 1
      } else if (dx > 0 && canPrev && travel > Math.max(width * 0.22, 60)) {
        decision = -1
      } else if (Math.abs(velocity) > 0.8 && travel > 40) {
        decision = velocity < 0 ? (canNext ? 1 : 0) : canPrev ? -1 : 0
      }

      if (decision === 0) {
        this.snapBack()
        return
      }
      this.slideOutAndChange(decision, drag)
    },
    slideOutAndChange(decision, drag) {
      this.mediaAnimated = false
      this.dragAnim = true
      // Outgoing file exits towards the drag direction (left for "next")
      this.dragOffset = (decision > 0 ? -1 : 1) * drag.width
      this.dragTimers.push(
        setTimeout(() => {
          this.changeFile(this.currentIndex + decision)
          // Incoming file must enter from the OPPOSITE side (right for "next")
          const incomingOffset = (decision > 0 ? 1 : -1) * drag.width
          this.dragAnim = false
          this.dragOffset = incomingOffset
          this.$nextTick(() => {
            // Let the browser paint the incoming side first, then animate to center
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.dragAnim = true
                this.dragOffset = 0
                this.dragTimers.push(
                  setTimeout(() => {
                    this.dragAnim = false
                  }, 230)
                )
              })
            })
          })
        }, 240)
      )
    },
    snapBack() {
      this.dragAnim = true
      this.dragOffset = 0
      this.dragTimers.push(
        setTimeout(() => {
          this.dragAnim = false
        }, 230)
      )
    },
    cancelDrag() {
      this.cancelFingerGesture()
    },
    cancelFingerGesture() {
      this.drag = null
      this.panStart = null
      this.pinchStart = null
      this.dragging = false
      this.dragOffset = 0
      this.dragAnim = false
    },
    startPanFromPointer(id) {
      const pointer = this.pointers[id]
      if (!pointer) {
        return
      }
      this.panStart = { id, x: pointer.x, y: pointer.y, tx: this.zoomX, ty: this.zoomY }
    },
    updatePan(e) {
      if (!this.panStart) {
        return
      }
      this.zoomX = this.panStart.tx + (e.clientX - this.panStart.x)
      this.zoomY = this.panStart.ty + (e.clientY - this.panStart.y)
      this.clampZoom()
    },
    startPinch() {
      const ids = Object.keys(this.pointers)
      if (ids.length < 2) {
        return
      }
      const rect = this.getTrackRect()
      const p1 = this.pointers[ids[0]]
      const p2 = this.pointers[ids[1]]
      const dx = p2.x - p1.x
      const dy = p2.y - p1.y
      this.zoomSettling = false
      this.pinchStart = {
        dist0: Math.hypot(dx, dy) || 1,
        scale0: this.zoomScale,
        tx0: this.zoomX,
        ty0: this.zoomY,
        cx: rect.cx,
        cy: rect.cy
      }
    },
    updatePinch() {
      const start = this.pinchStart
      if (!start) {
        return
      }
      const ids = Object.keys(this.pointers)
      if (ids.length < 2) {
        return
      }
      const p1 = this.pointers[ids[0]]
      const p2 = this.pointers[ids[1]]
      const dx = p2.x - p1.x
      const dy = p2.y - p1.y
      const dist = Math.hypot(dx, dy) || 1
      const scale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, start.scale0 * (dist / start.dist0)))
      const factor = scale / start.scale0
      const mx = (p1.x + p2.x) / 2
      const my = (p1.y + p2.y) / 2
      const fx = mx - start.cx
      const fy = my - start.cy
      this.zoomScale = scale
      this.zoomX = fx * (1 - factor) + start.tx0 * factor
      this.zoomY = fy * (1 - factor) + start.ty0 * factor
      this.clampZoom()
    },
    getTrackRect() {
      // Measure the untransformed media container: the media-track carries the
      // zoom transform (translate + scale), so getBoundingClientRect() on it
      // would return already-scaled dimensions and double-count the scale.
      const media = this.$refs.media
      const rect = media ? media.getBoundingClientRect() : { left: 0, top: 0, width: 0, height: 0 }
      return {
        cx: rect.left + rect.width / 2,
        cy: rect.top + rect.height / 2,
        width: rect.width,
        height: rect.height
      }
    },
    // Size of the area the current image actually occupies at scale 1
    // (object-contain may leave letterbox margins around it). Returns null
    // while the image is not loaded yet — callers then fall back to the
    // full container box.
    getImageDrawSize() {
      const track = this.$refs.track
      const img = track && track.querySelector('img')
      if (!img || !img.naturalWidth || !img.naturalHeight) {
        return null
      }
      const view = this.getTrackRect()
      if (!view.width || !view.height) {
        return null
      }
      const fit = Math.min(view.width / img.naturalWidth, view.height / img.naturalHeight)
      return {
        width: img.naturalWidth * fit,
        height: img.naturalHeight * fit
      }
    },
    clampZoom() {
      const scale = this.zoomScale
      const rect = this.getTrackRect()
      if (!rect.width || !rect.height) {
        return
      }
      if (scale <= ZOOM_MIN) {
        this.zoomScale = ZOOM_MIN
        this.zoomX = 0
        this.zoomY = 0
        return
      }
      // Clamp against the real drawn image, not the full box — otherwise the
      // letterbox margins scale with the image and its edge can never reach
      // the screen edge (it drifts past/inside instead of "catching").
      const drawn = this.getImageDrawSize()
      const drawW = drawn ? drawn.width : rect.width
      const drawH = drawn ? drawn.height : rect.height
      const maxX = Math.max(0, (scale * drawW - rect.width) / 2)
      const maxY = Math.max(0, (scale * drawH - rect.height) / 2)
      this.zoomX = maxX ? Math.max(-maxX, Math.min(maxX, this.zoomX)) : 0
      this.zoomY = maxY ? Math.max(-maxY, Math.min(maxY, this.zoomY)) : 0
    },
    toggleZoom(x, y) {
      if (!this.currentIsImage) {
        return
      }
      if (this.zoomScale > 1) {
        this.animateZoom(ZOOM_MIN, 0, 0)
        return
      }
      const target = 2
      const rect = this.getTrackRect()
      const fx = x - rect.cx
      const fy = y - rect.cy
      // Zooming from scale 1 with zero offset keeps the tapped point in place
      this.animateZoom(target, fx * (1 - target), fy * (1 - target))
    },
    animateZoom(scale, x, y) {
      this.cancelFingerGesture()
      this.zoomSettling = true
      this.$nextTick(() => {
        this.zoomScale = scale
        this.zoomX = x
        this.zoomY = y
        this.clampZoom()
        this.$nextTick(() => {
          this.dragTimers.push(
            setTimeout(() => {
              this.zoomSettling = false
            }, 240)
          )
        })
      })
    },
    resetZoom() {
      this.zoomSettling = false
      this.zoomScale = 1
      this.zoomX = 0
      this.zoomY = 0
    },
    resetZoomView() {
      this.resetZoom()
      this.cancelFingerGesture()
    },
    releasePointer(e) {
      delete this.pointers[e.pointerId]
      if (Object.keys(this.pointers).length > 0) {
        return
      }
      this.clearPointerListeners()
      this.drag = null
      this.panStart = null
      this.pinchStart = null
      this.dragging = false
    },
    applyRubberband(dx) {
      const canPrev = this.currentIndex > 0
      const canNext = this.currentIndex < this.files.length - 1
      const maxPull = 140
      // At an edge the pull is damped and capped. Keep it continuous (no jump)
      // when the offset saturates so the media never snaps sideways.
      if ((dx > 0 && !canPrev) || (dx < 0 && !canNext)) {
        return Math.sign(dx) * Math.min(Math.abs(dx) * 0.25, maxPull)
      }
      return dx
    },
    isOnVideoControls(e) {
      const target = e.target
      if (!target || target.tagName !== 'VIDEO') {
        return false
      }
      const rect = target.getBoundingClientRect()
      // Native player controls live in the bottom strip of the video element
      return e.clientY > rect.bottom - 80
    },
    clearPointerListeners() {
      window.removeEventListener('pointermove', this.onPointerMove)
      window.removeEventListener('pointerup', this.onPointerUp)
      window.removeEventListener('pointercancel', this.onPointerCancel)
    },
    changeFile(index) {
      this.resetZoomView()
      this.$emit('update:selectedFile', this.files[index])
    },
    close() {
      this.$emit('update:selectedFile', null)
    },
    nextFile() {
      this.mediaAnimated = true
      this.slideDir = 'next'
      if (this.files.length - 1 >= this.currentIndex + 1) {
        this.changeFile(this.currentIndex + 1)
      }
    },
    prevFile() {
      this.mediaAnimated = true
      this.slideDir = 'prev'
      if (this.currentIndex !== 0) {
        this.changeFile(this.currentIndex - 1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.LLFileModal {
  $root: &;
  @apply fixed left-0 top-0 w-full h-full p-8 flex items-center justify-center;

  &__background {
    @apply w-full h-full fixed bg-neutral-01-800 opacity-20;
  }
  &__block {
    @apply bg-white shadow-dropdown m-auto z-50 relative;
    width: calc(100vw - 4rem);
    height: calc(100vh - 4rem);
    .modal-content {
      @apply flex flex-col w-full h-full;
    }
  }
  &__header {
    @apply flex-shrink-0 flex items-center px-8 py-4 border-b border-neutral-01-50 min-w-0;
  }
  &__author {
    @apply min-w-0 pr-6;
  }
  &__header-actions {
    @apply flex items-center flex-shrink-0;
    margin-left: auto;
  }
  &__counter {
    @apply text-neutral-01-400 font-semibold mr-8 text-body-02 whitespace-nowrap;
  }
  &__close-button {
  }
  &__body {
    @apply flex flex-1 min-h-0;
  }
  &__media {
    @apply relative flex-1 min-w-0 flex items-center justify-center overflow-hidden;
    background-color: rgba(0, 0, 0, 0.85);
    touch-action: pan-y;
    &_zoomable {
      // Images handle their own pinch/double-tap gestures — keep the browser out
      touch-action: none;
    }
    &:hover {
      #{$root}__nav {
        opacity: 1;
        pointer-events: auto;
      }
    }
  }
  &__file {
    @apply w-full h-full;
    animation-duration: 0.28s;
    animation-timing-function: ease;
    animation-fill-mode: both;
    &_next {
      animation-name: LLFileModalSlideNext;
    }
    &_prev {
      animation-name: LLFileModalSlidePrev;
    }
  }
  @keyframes LLFileModalSlideNext {
    from {
      opacity: 0;
      transform: translateX(48px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
  @keyframes LLFileModalSlidePrev {
    from {
      opacity: 0;
      transform: translateX(-48px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
  &__media-track {
    @apply w-full h-full flex items-center justify-center relative;
    will-change: transform;
    &_dragging {
      transition: none;
    }
    &_anim {
      transition: transform 0.22s cubic-bezier(0.22, 0.8, 0.25, 1);
    }
    &_settle {
      transition: transform 0.22s ease;
    }
  }
  &__nav {
    @apply absolute top-1/2 z-10 flex items-center justify-center rounded-full cursor-pointer select-none;
    width: 48px;
    height: 48px;
    transform: translateY(-50%);
    opacity: 0;
    pointer-events: none;
    transition:
      opacity 0.2s ease,
      background-color 0.2s ease;
    background-color: rgba(255, 255, 255, 0.5);
    outline: none;
    &:hover {
      background-color: rgba(255, 255, 255, 0.85);
    }
    &:focus-visible {
      background-color: rgba(255, 255, 255, 0.85);
      opacity: 1;
      pointer-events: auto;
    }
    &-icon {
      @apply text-neutral-01-800 w-8 h-8;
    }
    &_prev {
      left: 24px;
    }
    &_next {
      right: 24px;
    }
  }
  @media (hover: none) {
    #{$root}__nav {
      display: none;
    }
  }

  @screen mob {
    @apply p-0;
    // Android Chrome resolves 100vh to the "large" viewport (URL bar hidden), so
    // a 100vh block overflows the actually visible area and the bottom of the
    // media (including the native video controls) lands off-screen. The dynamic
    // viewport unit tracks what is really visible; 100vh is the fallback.
    height: 100vh;
    height: 100dvh;
    #{$root}__block {
      width: 100%;
      height: 100%;
    }
    #{$root}__header {
      @apply px-4;
    }
    #{$root}__author {
      @apply pr-4 flex-1 min-w-0;
    }
    #{$root}__counter {
      @apply mr-4;
    }
    #{$root}__body {
      position: relative;
      display: block;
    }
    #{$root}__media {
      @apply w-full h-full;
      touch-action: pan-y;
    }
    #{$root}__nav {
      display: none;
    }
  }
}
</style>
